import { ReactNode, useMemo, useState } from 'react';

import { createSafeContext } from '../context/createSafeContext';

/**
 * The terminal's inner tabs. The built-in set is fixed; arbitrary string ids
 * are allowed for future MCP-app tabs (gated behind `features.mcpApps`).
 */
export type TerminalTab =
  | 'chat'
  | 'recents'
  | 'examples'
  | 'skills'
  | 'cron'
  | 'personalization'
  | (string & {});

export interface TabsState {
  readonly activeTab: TerminalTab;
}

export interface TabsActions {
  readonly setActiveTab: (tab: TerminalTab) => void;
}

export const [useTabsState, TabsStateProvider] =
  createSafeContext<TabsState>('TabsState');
export const [useTabsActions, TabsActionsProvider] =
  createSafeContext<TabsActions>('TabsActions');

interface Props {
  readonly children: ReactNode;
  readonly initialTab?: TerminalTab;
}

export function TabsProvider({ children, initialTab = 'chat' }: Props) {
  const [activeTab, setActiveTab] = useState<TerminalTab>(initialTab);
  const state = useMemo<TabsState>(() => ({ activeTab }), [activeTab]);
  const actions = useMemo<TabsActions>(() => ({ setActiveTab }), []);
  return (
    <TabsStateProvider value={state}>
      <TabsActionsProvider value={actions}>{children}</TabsActionsProvider>
    </TabsStateProvider>
  );
}
